/* Product-specific pack UI. Native cart submission requires an explicit release flag. */
(function (root) {
  'use strict';
  function quote(offers, variantId, quantity) {
    const q = Number(quantity), offer = offers?.[String(variantId)];
    if (!offer || !Number.isInteger(q) || q < 1 || q > 4) return null;
    const total = offer.totals?.[q - 1], base = offer.totals?.[0];
    if (!Number.isInteger(total) || total <= 0 || !Number.isInteger(base)) return null;
    const shipping = total < 5000 ? 1000 : 0;
    return { quantity: q, merchandise: total, shipping, delivered: total + shipping,
      each: Math.round(total / q), saving: Math.max(0, base * q - total) };
  }
  function quoteMixed(offers, ids, packPrices) {
    if (!Array.isArray(ids) || ids.length < 1 || ids.length > 4) return null;
    const parts = ids.map(id => quote(offers, id, ids.length));
    if (parts.some(p => !p)) return null;
    const key = ids.map(String).sort().join(',');
    const merchandise = ids.length > 1 && packPrices != null
      ? packPrices[key]
      : Math.round(parts.reduce((sum, p) => sum + p.merchandise, 0) / ids.length);
    // A configured combination must have an exact price. Never silently average
    // unequal pack prices when its authoritative mapping is missing or invalid.
    if (!Number.isInteger(merchandise) || merchandise <= 0) return null;
    const base = ids.reduce((sum,id) => sum + offers[String(id)].totals[0], 0);
    const shipping = merchandise < 5000 ? 1000 : 0;
    return {quantity:ids.length, merchandise, shipping, delivered:merchandise+shipping,
      each:Math.round(merchandise/ids.length), saving:Math.max(0,base-merchandise)};
  }
  function nativeItem(config, ids) {
    if (!Array.isArray(ids) || ids.length<1 || ids.length>4 || ids.some(id=>!config?.offers?.[String(id)])) return null;
    const key=ids.map(String).sort().join(',');
    const id=ids.length===1 ? String(ids[0]) : config.native_pack_variants?.[key];
    return id && /^\d+$/.test(String(id)) ? {id:String(id),quantity:1} : null;
  }
  // Merchant-chosen initial pack (custom.parrot_pack_config.default_pack_quantity); anything else keeps one perch.
  function defaultPackQuantity(config) {
    const q = Number(config?.default_pack_quantity);
    return Number.isInteger(q) && q >= 1 && q <= 4 ? q : 1;
  }
  // Merchant-chosen starting length for each perch slot (custom.parrot_pack_config.default_pack_variant_ids).
  // Unknown or unavailable IDs fall back to the first available length.
  function initialSelection(config, fallbackId, isAvailable = () => true) {
    const ids = Array.isArray(config?.default_pack_variant_ids) ? config.default_pack_variant_ids : [];
    const pick = i => {
      const id = ids[i] == null ? '' : String(ids[i]);
      return id && config?.offers?.[id] && isAvailable(id) ? id : null;
    };
    return [pick(0) || String(fallbackId), pick(1), pick(2), pick(3)];
  }
  // Visitor-level test of the starting pack (custom.parrot_pack_config.default_pack_ab). Applies only while the
  // page's experiment id matches and the visitor's group is listed; otherwise the merchant default stands.
  function abDefault(config, context) {
    const test = config?.default_pack_ab;
    if (!test || !context || !test.experiment_id || String(context.experiment_id || '') !== String(test.experiment_id)) return null;
    const group = String(context.group || ''), arm = test.groups?.[group];
    const q = Number(arm?.default_pack_quantity);
    if (!Number.isInteger(q) || q < 1 || q > 4) return null;
    const ids = Array.isArray(arm.default_pack_variant_ids) ? arm.default_pack_variant_ids : config.default_pack_variant_ids;
    return { experiment_id: String(test.experiment_id), group, default_pack_quantity: q, default_pack_variant_ids: ids,
      sticky_price: arm.sticky_price === 'each' ? 'each' : '', cart_tag: `${test.experiment_id}|${group}|default_${q}` };
  }
  // Bottom-bar wording. A test group may show the per-perch price of a multi-perch pack instead of the pack total.
  function stickySummary(selection, arm, money, title) {
    return arm?.sticky_price === 'each' && selection.quantity > 1
      ? { title: `${selection.quantity} Perches`, price: `${money(selection.each)} each` }
      : { title, price: money(selection.merchandise) };
  }
  // Shipping and currency wording by the storefront's market country. US copy is unchanged;
  // other markets see prices with the USD code and no "US shipping" wording.
  function regionCopy(country) {
    const domestic = String(country || 'US').toUpperCase() === 'US';
    const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencyDisplay: domestic ? 'symbol' : 'code' }).format(cents / 100);
    const fee = domestic ? '$10' : money(1000);
    return {
      domestic, money,
      card: (q, merchandise) => (Number.isInteger(merchandise) ? merchandise < 5000 : q <= 2)
        ? ` · ${fee} shipping` : (domestic ? ' · Free US shipping' : ' · Free shipping'),
      badge: paid => paid ? (domestic ? '$10 US shipping' : `${fee} shipping`) : (domestic ? 'Free US shipping' : 'Free shipping'),
      total: paid => paid ? `${fee} standard shipping` : (domestic ? 'US standard shipping included' : 'Standard shipping included'),
      line: paid => paid
        ? (domestic ? '$10 US standard shipping. Free on merchandise orders $50+.' : `${fee} standard shipping. Free on merchandise orders of ${money(5000)} or more.`)
        : (domestic ? 'US standard shipping included with this pack.' : 'Standard shipping included with this pack.'),
      note: domestic ? '' : 'Prices are in US dollars (USD).'
    };
  }
  if (typeof module !== 'undefined') module.exports = { quote, quoteMixed, nativeItem, defaultPackQuantity, initialSelection, abDefault, stickySummary, regionCopy };
  if (!root.document) return;
  const cfg = root.LM_PDP_REVIEW_PREVIEW;
  if (!cfg || (cfg.review_only !== true && cfg.native_cart_enabled !== true) || !cfg.product_handle) return;
  const section = document.querySelector('[data-product-section]');
  if (!section || section.dataset.productHandle !== cfg.product_handle) return;
  const productNode = section.querySelector('[data-product-json]');
  const product = JSON.parse(productNode.textContent);
  const region = regionCopy(root.Shopify?.country);
  const money = region.money;
  const e = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text) n.textContent = text; return n; };
  const form = section.querySelector('[data-product-form]');
  const slot = section.querySelector('[data-lm-pdp-slot="upsell"]');
  const abContext = root.LM_AB?.getContext?.();
  const treatment = abContext?.baseline_version_key === cfg.version_key || root.LM_AB?.getActiveParams?.()?.landing_page_version_key === cfg.version_key;
  const abArm = treatment ? abDefault(cfg, abContext) : null;
  const startCfg = abArm ? { ...cfg, ...abArm } : cfg;
  section.querySelectorAll('[data-product-gallery] img').forEach(img=>{img.width=1254;img.height=1254;});
  const availableIds = new Set(product.variants.filter(v => v.available).map(v => String(v.id)));
  const selectedIds = initialSelection(startCfg, String(product.variants.find(v=>v.available)?.id || product.variants[0].id), id => availableIds.has(id));
  let lengthRows;
  if (treatment) {
    section.classList.add('lm-mixed-review');
    section.querySelectorAll('*').forEach(n=>{if(!n.children.length && n.textContent.trim()==='Free shipping $50+')n.setAttribute('data-review-shipping-badge','');});
    section.querySelectorAll('[data-option-input]').forEach(n => { const group=n.closest('fieldset'); if(group) group.hidden=true; });
    const h1 = section.querySelector('h1'); if (h1) h1.textContent = cfg.display_title;
    const stickyTitle = section.querySelector('.lm-sticky-atc__summary strong'); if (stickyTitle) stickyTitle.textContent=cfg.display_title;
    const picker = e('fieldset', 'lm-offer-picker lm-quantity-tier-picker');
    picker.setAttribute('data-offer-picker', ''); picker.setAttribute('data-quantity-tier-picker', '');
    picker.append(e('legend', 'lm-offer-picker__legend', 'How many perches?'));
    const reasons = ['Start with one', 'One now. One spare.', 'Two spots. One spare.', 'For your flock and spares'];
    const initialPack = defaultPackQuantity(startCfg);
    for (let q = 1; q <= 4; q++) {
      const label = e('label', 'lm-offer-card'); label.dataset.lmOfferTier = q;
      const input = e('input', 'lm-offer-card__input'); input.type = 'radio'; input.name = 'review-pack'; input.value = q; input.checked = q === initialPack; input.setAttribute('data-offer-input', '');
      const body = e('span', 'lm-offer-card__body'), top = e('span', 'lm-offer-card__top'), price = e('strong', 'lm-offer-card__price'); price.setAttribute('data-tier-price', '');
      const tierLabel=e('span', 'lm-offer-card__title', `${q} ${q === 1 ? 'Perch' : 'Perches'}`); tierLabel.setAttribute('data-lm-offer-tier-label',''); top.append(tierLabel, price);
      const unit = e('span'); unit.setAttribute('data-lm-offer-tier-unit-price', '');
      const shipping = e('span', 'lm-offer-card__help');
      const shippingCopy = e('span', '', region.card(q)); shippingCopy.setAttribute('data-lm-offer-tier-shipping', '');
      shipping.append(unit, shippingCopy);
      const saving = e('span', 'lm-offer-card__help'); saving.setAttribute('data-lm-offer-tier-savings', '');
      const reason=e('span', 'lm-offer-card__help', reasons[q - 1]); reason.setAttribute('data-lm-offer-tier-reason',''); shipping.setAttribute('data-lm-offer-tier-meta',''); body.append(top, reason, shipping, saving);
      label.append(input, body); if (q === 3) body.prepend(e('span', 'lm-offer-card__badge', 'Recommended'));
      picker.append(label);
    }
    const qty = e('input'); qty.type = 'hidden'; qty.name = 'quantity'; qty.value = String(initialPack); qty.setAttribute('data-offer-quantity', '');
    lengthRows=e('fieldset','lm-pack-lengths');
    lengthRows.append(e('legend','lm-offer-picker__legend','Choose each length'));
    for(let i=0;i<4;i++) {
      const row=e('div','lm-pack-length-row'); row.hidden=i>0;
      const label=e('label','',`Perch ${i+1}`);label.htmlFor=`lm-perch-length-${i}`;
      const select=e('select');select.id=label.htmlFor;select.dataset.perchIndex=i;
      for(const v of product.variants) {
        const option=e('option','',v.options.join(' / '));option.value=String(v.id);option.disabled=!v.available;select.append(option);
      }
      select.value=selectedIds[0];row.append(label,select);lengthRows.append(row);
    }
    slot.replaceChildren(picker,lengthRows,qty);
    section.addEventListener('change',event=>{
      const select=event.target.closest('[data-perch-index]');
      if(select) selectedIds[Number(select.dataset.perchIndex)]=select.value;
      sync();
    });
    function mountVideo() {
    const videoArea=section.querySelector('[data-lm-pdp-details-content] article');
    if(videoArea && !videoArea.querySelector('.lm-perch-demo,.lm-video-pending')) {
      if(cfg.demo_video_url) {
        const video=e('video','lm-perch-demo'); video.controls=true;video.playsInline=true;video.preload='none';video.src=cfg.demo_video_url;
        // Posters are not lazy-loaded by the browser; set it near the viewport.
        if(cfg.demo_video_poster) {
          if('IntersectionObserver' in root) {
            const posterObserver=new IntersectionObserver(entries=>{
              if(!entries.some(entry=>entry.isIntersecting)) return;
              video.poster=cfg.demo_video_poster;posterObserver.disconnect();
            },{rootMargin:'800px 0px'});
            posterObserver.observe(video);
          } else video.poster=cfg.demo_video_poster;
        }
        video.setAttribute('aria-label','Parrot perch in use');
        const media=e('figure','lm-pdp-detail-module__media lm-perch-demo-media');
        media.append(video);
        videoArea.classList.remove('lm-pdp-detail-module--text-only');
        videoArea.classList.add('lm-pdp-detail-module--with-media','lm-pdp-detail-module--media-right');
        videoArea.append(media);
      }
    }
    }
    mountVideo();
    const videoObserver=new MutationObserver(mountVideo);
    videoObserver.observe(section.querySelector('[data-lm-pdp-slot="details"]') || section,{childList:true,subtree:true});
    const basePricing = root.LMQuantityTiers?.price;
    root.LMQuantityTiers = { price(config, base, q) {
      if (config?.review_price_matrix) {
        const totals = config.review_price_matrix[String(base)]; const total = totals?.[Number(q) - 1];
        if (Number.isInteger(total)) return { totalCents: total, compareCents: base * Number(q), unitCents: Math.round(total / Number(q)), discountPercentage: 0 };
      }
      return basePricing?.(config, base, q) || null;
    } };
  }
  function selection() {
    const opts = [...section.querySelectorAll('[data-option-input]:checked')].map(n => n.value);
    const variant = product.variants.find(v => v.options.every((o, i) => o === opts[i])) || product.variants[0];
    const quantity = treatment ? Number(section.querySelector('[data-offer-input]:checked')?.value || 1) : Number(form?.querySelector('[name="quantity"]')?.value || 1);
    for(let i=0;i<quantity;i++) if(!selectedIds[i]) selectedIds[i]=selectedIds[0];
    const priced = treatment ? quoteMixed(cfg.offers, selectedIds.slice(0,quantity), cfg.native_pack_prices) : { quantity, merchandise: variant.price * quantity, shipping: variant.price * quantity >= 5000 ? 0 : 1000 };
    if (!priced) return null;
    return { ...priced, delivered: priced.merchandise + priced.shipping, variant, lengths: treatment ? selectedIds.slice(0,quantity).map(id=>cfg.offers[id].length) : [variant.options.join(' / ')], length: treatment ? cfg.offers[selectedIds[0]].length : variant.options.join(' / ') };
  }
  const total = e('p', 'lm-microcopy lm-review-total'); total.setAttribute('aria-live', 'polite'); total.setAttribute('aria-atomic', 'true'); if (treatment) slot.append(total);
  function sync() {
    const s = selection(); if (!s) return;
    if (treatment) {
      // Keep review quotes authoritative across older and current shared theme runtimes.
      section.querySelectorAll('[data-lm-offer-tier]').forEach(card => {
        const count=Number(card.dataset.lmOfferTier), active=count===s.quantity;
        const ids=Array.from({length:count},(_,i)=>selectedIds[i] || selectedIds[0]);
        const offer = active ? s : quoteMixed(cfg.offers, ids, cfg.native_pack_prices);
        card.classList.toggle('is-selected',active);
        if (!offer) return;
        card.querySelector('[data-tier-price]').textContent = money(offer.merchandise);
        card.querySelector('[data-lm-offer-tier-unit-price]').textContent = money(offer.each) + ' each';
        card.querySelector('[data-lm-offer-tier-savings]').textContent = offer.saving ? 'Save ' + money(offer.saving) : 'Standard price';
        card.querySelector('[data-lm-offer-tier-shipping]').textContent = region.card(count, offer.merchandise);
      });
      lengthRows.querySelectorAll('.lm-pack-length-row').forEach((row,i)=>{
        row.hidden=i>=s.quantity;const select=row.querySelector('select');select.disabled=i>=s.quantity;select.value=selectedIds[i]||selectedIds[0];
      });
      section.querySelectorAll('[data-price]').forEach(n => { n.textContent = money(s.merchandise); });
      const sticky = stickySummary(s, abArm, money, cfg.display_title);
      section.querySelectorAll('[data-sticky-price]').forEach(n => { n.textContent = sticky.price; });
      const stickyName = section.querySelector('.lm-sticky-atc__summary strong'); if (stickyName) stickyName.textContent = sticky.title;
      section.querySelectorAll('[data-compare-price]').forEach(n=>{n.textContent=money(s.merchandise+s.saving);n.hidden=!s.saving;});
      section.querySelectorAll('[data-save-price]').forEach(n=>{n.textContent='Save '+money(s.saving);n.hidden=!s.saving;});
      section.querySelectorAll('[data-review-shipping-badge]').forEach(n=>{n.textContent=region.badge(Boolean(s.shipping));});
      const quantityInput=form?.querySelector('[data-offer-quantity]'); if(quantityInput) quantityInput.value=String(s.quantity);
      total.textContent = `${s.quantity} ${s.quantity === 1 ? 'perch' : 'perches'} · ${s.lengths.join(' + ')} · Total: ${money(s.delivered)} · ${region.total(Boolean(s.shipping))}${region.note ? ' · ' + region.note : ''}`;
      const line = section.querySelector('[data-product-shipping-price]'); if (line) line.textContent = region.line(Boolean(s.shipping));
    }
  }
  const dialog = e('dialog', 'lm-review-selection'); dialog.setAttribute('aria-label', 'Your selection');
  const title = e('h2', '', 'Your selection'), description = e('p'), breakdown = e('p'), close = e('button', 'lm-button', 'Back to product'); close.type = 'button';
  dialog.append(title, description, breakdown, close); document.body.append(dialog); close.onclick = () => dialog.close();
  function show(event) {
    if(cfg.native_cart_enabled === true) {
      if(!treatment || event.target.closest?.('[data-cart-checkout]')) return;
      const selected=selection();
      if(selected && nativeItem(cfg,selectedIds.slice(0,selected.quantity))) return;
      event.preventDefault();event.stopImmediatePropagation();
      const error=form?.querySelector('[data-form-error]');
      if(error){error.textContent='This combination is unavailable. Please choose another.';error.hidden=false;}
      return;
    }
    event.preventDefault(); event.stopImmediatePropagation(); const s = selection(); if (!s) return;
    description.textContent = `${s.quantity} ${s.quantity === 1 ? 'perch' : 'perches'} · ${s.lengths.map((length,i)=>`Perch ${i+1}: ${length}`).join(' · ')}`;
    breakdown.textContent = `Products ${money(s.merchandise)} + shipping ${money(s.shipping)} = ${money(s.delivered)}`;
    dialog.showModal(); root.LM_REVIEW_SELECTION = { ...s, variant: undefined };
  }
  // Reuse the theme's real cart/checkout handlers; only replace the submitted variant.
  form?.addEventListener('formdata',event=>{
    if(cfg.native_cart_enabled !== true || !treatment) return;
    const selected=selection();
    const item=selected && nativeItem(cfg,selectedIds.slice(0,selected.quantity));
    if(!item) return;
    event.formData.set('id',item.id);event.formData.set('quantity','1');event.formData.delete('properties[_offer]');
    // Tag the added line with the starting-pack group so the order carries it. It travels in the add request itself
    // (a separate cart update can race the add and land on another cart); the leading underscore hides it from shoppers.
    if(abArm) event.formData.set('properties[_lm_pack_default_test]',abArm.cart_tag);
  });
  const buttonObserver=new MutationObserver(changes=>{
    if(changes.some(c=>c.target.matches?.('[data-add-to-cart],[data-direct-checkout],[data-sticky-add-to-cart],[data-sticky-checkout]')))requestAnimationFrame(sync);
  });
  buttonObserver.observe(section,{subtree:true,attributes:true,attributeFilter:['disabled']});
  form?.addEventListener('submit', show, true);
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-direct-checkout], [data-sticky-checkout], [data-sticky-add], [data-sticky-add-to-cart], [data-cart-checkout]');
    if (button) show(event);
  }, true);
  section.addEventListener('change', () => requestAnimationFrame(sync));
  // Deferred shared pricing/experiment initialization can run after this adapter.
  // Reapply the exact mixed-pack quote and the existing per-piece sticky wording.
  root.addEventListener('lm:product-price-synced', () => requestAnimationFrame(sync));
  sync(); root.LM_PDP_REVIEW_READY = true;
  // The shared theme re-prices the single variant while it initialises; re-apply the pack quote afterwards
  // so a mixed-length default (e.g. 50 + 60 cm) shows its own savings from the first paint.
  requestAnimationFrame(sync);
  if (document.readyState === 'complete') setTimeout(sync, 0);
  else root.addEventListener('load', () => requestAnimationFrame(sync), { once: true });
})(typeof window !== 'undefined' ? window : globalThis);
